import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import Logo from "../images/Logo.png";
import CoralReef from "../images/CoralReef.jpg";
import NetworkCanvas from "./NetworkCanvas";
import { isUserLoggedIn } from "../service/AuthService";
import { request } from "../service/HttpService";
import { errorMessage } from "../service/HttpErrors";
import { decideOAuth, getOAuthClient, rememberConsentPage } from "../service/OAuthService";

// The query an AI app sends; passed back untouched on approve, where the
// backend checks all of it again
const PARAMS = ["client_id", "redirect_uri", "response_type", "code_challenge", "code_challenge_method", "state"];

/**
 * The OAuth authorization endpoint for the MCP server: an AI app like Claude
 * sends the user here to let it read their papers. Signed-out visitors sign in
 * (or sign up) first and are brought back by takeConsentReturn.
 */
export default function OAuthAuthorize() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const params = useMemo(
        () => Object.fromEntries(PARAMS.map((key) => [key, searchParams.get(key)])),
        [searchParams]
    );
    const [client, setClient] = useState(null);
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isUserLoggedIn()) {
            rememberConsentPage();
            navigate("/login", { replace: true });
            return;
        }
        getOAuthClient(params.client_id, params.redirect_uri)
            .then(setClient)
            .catch((err) => setError(errorMessage(err,
                "This connection request isn't valid. Start again from the app you're connecting.")));
        request("GET", "/users/userinfo")
            .then((user) => setEmail(user?.email || ""))
            .catch(() => { /* only shown for reassurance */ });
    }, [params, navigate]);

    const decide = async (approve) => {
        setBusy(true);
        setError("");
        try {
            const { redirectUrl } = await decideOAuth(params, approve);
            window.location.assign(redirectUrl);
        } catch (err) {
            setError(errorMessage(err, "Couldn't finish connecting. Please try again."));
            setBusy(false);
        }
    };

    return (
        <div className="auth-layout">
            {/* Left panel */}
            <div className="auth-panel" style={{ backgroundImage: `url(${CoralReef})` }}>
                <div className="auth-panel-overlay" />
                <NetworkCanvas />
                <div className="auth-panel-content">
                    <div className="auth-logo-wrap">
                        <img src={Logo} alt="Logo" className="auth-logo" />
                    </div>
                    <h2 className="auth-panel-title">DeepThought</h2>
                    <p className="auth-panel-sub">Research analytics for the modern scientist.</p>
                </div>
            </div>

            {/* Right: the consent itself */}
            <div className="auth-form-side">
                <NetworkCanvas dark />
                <div className="auth-form-wrap">
                    {!client && !error && <p className="auth-sub">Loading…</p>}

                    {!client && error && (
                        <>
                            <h1 className="auth-heading">Can't connect</h1>
                            <p className="chpw-error" style={{ marginBottom: 20 }}>{error}</p>
                            <button className="sub-action-btn sub-action-outline" onClick={() => navigate("/")}>
                                Go to DeepThought
                            </button>
                        </>
                    )}

                    {client && (
                        <>
                            <h1 className="auth-heading">Connect {client.clientName}</h1>
                            <p className="auth-sub">
                                {client.clientName} wants to access your DeepThought account
                                {email && <> (<strong>{email}</strong>)</>}.
                            </p>

                            <p className="oauth-consent-label">It will be able to</p>
                            <ul className="oauth-consent-list">
                                <li><Check size={15} className="oauth-consent-yes" />See your papers and their Novelty, Methodology and Impact scores</li>
                                <li><Check size={15} className="oauth-consent-yes" />Read DeepThought's analysis, similar papers and DeepThinkers for them</li>
                                <li><Check size={15} className="oauth-consent-yes" />Submit papers for scoring (they count toward your daily limit)</li>
                            </ul>
                            <p className="oauth-consent-label">It won't be able to</p>
                            <ul className="oauth-consent-list">
                                <li><X size={15} className="oauth-consent-no" />Change, share or delete anything</li>
                            </ul>

                            <p className="oauth-consent-note">
                                You'll return to <strong>{client.redirectHost}</strong>, so only continue if you
                                started this there. You can disconnect it any time in Settings → Privacy.
                            </p>

                            {error && <p className="chpw-error" style={{ marginBottom: 14 }}>{error}</p>}

                            <div className="oauth-consent-actions">
                                <button className="sub-action-btn sub-action-outline" disabled={busy} onClick={() => decide(false)}>
                                    Cancel
                                </button>
                                <button className="auth-submit-btn" disabled={busy} onClick={() => decide(true)}>
                                    {busy ? "Connecting…" : "Allow access"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
